import { Analytics } from '@cobliteam/analytics';
import { Location } from '@cobliteam/common-types';
import { useRiskRecognitionHighlight } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions';
import { useCreateRiskRecognitionSilence } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions/useCreateRiskRecognitionSilence';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import Drawer, { DrawerBody, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@cobliteam/ui/components/Drawer';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { parseISO } from '@cobliteam/utils/date';
import { createSerializer, parseAsInteger, parseAsString } from 'nuqs';
import { useHistory } from 'react-router-dom';
import { buildRiskRecognitionInvestigatePrompt } from '../../lib/pageContext';
import { useAIAssistant } from '../../store/useAIAssistant';
import useRiskRecognition, { buildRiskRecognitionEventProps } from '../../store/useRiskRecognition';
import { toDriverDetails, toDriverTitleCase, toInvestigation } from './mockInvestigation';
import RiskRecognitionDriverDetails from './RiskRecognitionDriverDetails';
import RiskRecognitionInvestigation from './RiskRecognitionInvestigation';

// Type-safe builder for the /tracking deep link. `date` is epoch ms, read back as a number on /tracking.
const serializeTrackingLink = createSerializer({
  vehicle_id: parseAsString,
  date: parseAsInteger,
});

const RiskRecognitionDrawer = () => {
  const { t } = useTranslation('ai');
  const { aiRiskRecognitionHighlights } = useFeatureFlags();
  const isDrawerOpen = useRiskRecognition((s) => s.isDrawerOpen);
  // Read-only consumer of the cached highlight (enabled defaults false — the card drives polling).
  const { data: highlight } = useRiskRecognitionHighlight();
  const closeDrawer = useRiskRecognition((s) => s.closeDrawer);
  const followRouteToTracking = useRiskRecognition((s) => s.followRouteToTracking);
  const history = useHistory();
  const { openAISidepanelWithHiddenInput, resetNewThreadId, setThreadId } = useAIAssistant();
  const { mutate: createSilence } = useCreateRiskRecognitionSilence();

  if (!aiRiskRecognitionHighlights) return null;

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    Analytics.track('AI - Risk Recognition - Drawer Closed', buildRiskRecognitionEventProps(highlight));
    closeDrawer();
  };

  const handleViewRoute = () => {
    Analytics.track('AI - Risk Recognition - View Route Clicked', buildRiskRecognitionEventProps(highlight));
    const vehicleId = highlight?.metadata?.vehicle_id;
    if (!vehicleId) return;
    const locatedAt = highlight?.metadata?.last_location?.located_at;
    // parseISO keeps timezone parsing deterministic across browsers (Safari chokes on raw Date.parse).
    const parsed = locatedAt ? parseISO(locatedAt).getTime() : Number.NaN;
    const date = Number.isNaN(parsed) ? Date.now() : parsed;
    // Pin this bucket so the floating card keeps following the acted-on alert onto /tracking.
    followRouteToTracking(highlight?.time_bucket_end ?? null);
    closeDrawer();
    history.push(serializeTrackingLink(Location.TRACKING, { vehicle_id: vehicleId, date }));
  };

  const handleInvestigate = async () => {
    if (!highlight) return;
    Analytics.track('AI - Risk Recognition - Investigate Clicked', buildRiskRecognitionEventProps(highlight));
    Analytics.track('AI - Sidepanel - Opened', {
      source: 'risk-recognition-drawer',
      deviceId: highlight.metadata?.device_id,
      vehicleId: highlight.metadata?.vehicle_id,
    });

    const mainFactorTitle = highlight.risk_factors.find((factor) => factor.type === 'main')?.title ?? highlight.risk_factors[0]?.title;
    const prompt = buildRiskRecognitionInvestigatePrompt({
      driverName: highlight.metadata?.driver_name ? toDriverTitleCase(highlight.metadata.driver_name) : undefined,
      mainFactorTitle,
      driverId: highlight.metadata?.driver_id,
      vehicleId: highlight.metadata?.vehicle_id,
      licensePlate: highlight.metadata?.vehicle_plate,
      deviceId: highlight.metadata?.device_id,
      ruleName: highlight.metadata?.rule_name,
    });

    closeDrawer();
    await setThreadId('');
    resetNewThreadId();
    openAISidepanelWithHiddenInput(prompt);
  };

  // The anchor's `href` handles the WhatsApp navigation; here we record the
  // fleet-wide silence (~1h, contacted) alongside analytics. Fired from both
  // WhatsApp entry points (body link + footer button) — server is idempotent on
  // (detection_id, reason). Guarded: no detection_id → skip the POST, still navigate.
  const handleTalkToDriver = () => {
    Analytics.track('AI - Risk Recognition - Talk To Driver Clicked', buildRiskRecognitionEventProps(highlight));
    const detectionId = highlight?.metadata?.detection_id;
    if (detectionId) createSilence({ detectionId, reason: 'contacted' });
  };

  const driverDetails = highlight ? toDriverDetails(highlight) : null;
  const investigation = highlight ? toInvestigation(highlight) : null;
  const driverWhatsAppUrl = driverDetails?.whatsAppUrl ?? '';

  return (
    <Drawer open={isDrawerOpen} onOpenChange={handleOpenChange}>
      <DrawerContent size='md'>
        <DrawerHeader>
          <div className='flex items-center gap-2'>
            <DrawerTitle>{t('risk_recognition.drawer.title')}</DrawerTitle>
            <Icon name='info' size='md' className='text-[#60646c]' aria-hidden />
          </div>
          <DrawerClose />
        </DrawerHeader>

        <DrawerBody className='min-h-0 flex-1 flex-col gap-3 bg-white pt-6'>
          {driverDetails && <RiskRecognitionDriverDetails data={driverDetails} onViewRoute={handleViewRoute} onTalkToDriver={handleTalkToDriver} />}
          {investigation && <RiskRecognitionInvestigation data={investigation} />}
        </DrawerBody>

        <DrawerFooter className='items-center justify-between'>
          <DrawerClose asChild>
            <button
              type='button'
              className='inline-flex h-8 items-center rounded-md px-2 transition-colors hover:bg-[#dcebfe] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
            >
              <Typography as='span' variant='text-md-semibold' className='text-[#1c53e6]'>
                {t('risk_recognition.drawer.close')}
              </Typography>
            </button>
          </DrawerClose>

          <div className='flex items-center gap-2'>
            {/* The span wrapper lets the tooltip fire over the disabled button. */}
            {driverWhatsAppUrl ? (
              <a
                href={driverWhatsAppUrl}
                target='_blank'
                rel='noopener noreferrer'
                onClick={handleTalkToDriver}
                className='inline-flex h-9 items-center rounded-lg bg-[#dcebfe] px-4 transition-colors hover:bg-[#cfe2fb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
              >
                <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#07074f]'>
                  {t('risk_recognition.drawer.talk_to_driver')}
                </Typography>
              </a>
            ) : (
              <Tooltip content={t('risk_recognition.drawer.no_driver_phone')} position='top'>
                <span className='inline-flex'>
                  <button type='button' disabled className='inline-flex h-9 cursor-not-allowed items-center rounded-lg bg-[#dcebfe] px-4 opacity-50'>
                    <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#07074f]'>
                      {t('risk_recognition.drawer.talk_to_driver')}
                    </Typography>
                  </button>
                </span>
              </Tooltip>
            )}

            <button
              type='button'
              onClick={handleInvestigate}
              className={cn(
                '[background-image:linear-gradient(248.25deg,#8b5cf6_20%,#0248e9_60%,#001bb8_100%)]',
                'inline-flex h-9 items-center gap-2 rounded-lg px-4 transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
              )}
            >
              <Icon name='search' size='sm' className='text-[#fafafa]' aria-hidden />
              <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#fafafa]'>
                {t('risk_recognition.drawer.investigate')}
              </Typography>
            </button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default RiskRecognitionDrawer;
